import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";

const currencySymbols: Record<string, string> = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    JPY: "¥",
    AUD: "$",
    CAD: "$",
    CNY: "¥",
    INR: "₹",
};

const formatAmount = (amount: number, currency = "USD") => {
    const symbol = currencySymbols[currency] || "$";
    
    if(!amount) {
        return `${symbol}0.00`;
    }

    return `${symbol}${amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
};

const periodLabels: Record<string, string> = {
    today: "Today",
    last7Days: "Last 7 Days",
    last30Days: "Last 30 Days",
    thisMonth: "This Month",
    lastMonth: "Last Month",
    customRange: "Custom Range",
};

const CommissionChart: React.FC<{
    balance: any;
    selectedPeriod: string;
    selectedDates?: Date[];
}> = ({ balance, selectedPeriod, selectedDates }) => {
    const assetList = balance.map((item: any) => item.asset);
    const chartData = balance.map((item: any) => {
        const value = item.available;
        if (!value) return 0;
        return parseFloat(value) || 0;
    });

    // Get theme primary color from CSS variable (cached)
    const chartPrimaryColor = useMemo(() => {
        return getComputedStyle(document.documentElement)
            .getPropertyValue('--chart-primary-color').trim() || "#3CCF91";
    }, []);

    const series = [
        {
            name: periodLabels[selectedPeriod] || selectedPeriod,
            data: chartData,
        },
    ];

    const options: ApexCharts.ApexOptions = {
        colors: [chartPrimaryColor],
        chart: {
            type: "bar",
            toolbar: { show: false },
            animations: {
                enabled: true,
                speed: 500,
                animateGradually: {
                    enabled: true,
                    delay: 150
                },
                dynamicAnimation: {
                    enabled: true,
                    speed: 350
                }
            }
        },
        plotOptions: {
            bar: {
                borderRadius: 7,
                columnWidth: '40px'
            }
        },
        dataLabels: {
            enabled: false,
        },
        stroke: {
            show: true,
            width: 2,
            colors: ["transparent"],
        },
        xaxis: {
            categories: assetList,
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false,
            },
            labels: {
                style: {
                    fontWeight: 'bold',
                    colors: "#1B254B",
                    fontFamily: 'inherit',
                }
            },
        },
        yaxis: {
            labels: {
                show: false,
            },
        },
        fill: {
            type: "gradient",
            gradient: {
                shade: "light",
                type: "vertical",
                shadeIntensity: 0.7,
                inverseColors: false,
                opacityFrom: 1,
                opacityTo: 0,
                stops: [0, 100],
            },
        },
        tooltip: {
            custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                const value = series[seriesIndex][dataPointIndex];
                const category = w.globals.labels[dataPointIndex];

                // Check if it's a crypto asset
                const isCrypto = balance[dataPointIndex]?.crypto;
                const formattedValue = isCrypto
                    ? value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })
                    : formatAmount(value, category);

                return `
            <div style="
                padding: 8px 12px;
                background: #1f2937;
                border-radius: 8px;
                box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
            ">
                <div style="color: #9CA3AF; font-size: 12px; font-weight: 500;">
                    ${category}
                </div>
                <div style="color: #ffffff; font-size: 14px; font-weight: bold;">
                    ${formattedValue}
                </div>
            </div>
        `;
            },
        },
        grid: {
            yaxis: {
                lines: {
                    show: false,
                },
            },
        },
        legend: {
            show: false,
        }
    };

    return (
        <div className="w-full">
            <ReactApexChart options={options} series={series} type="bar" height={275} />
        </div>
    );
}

export default CommissionChart;